// src/components/ModalRegistroEgreso.js
import React, { useState, useEffect, useMemo } from 'react';
import { 
    FaTimes, FaExclamationTriangle, FaCheckCircle, FaInfoCircle, 
    FaUpload, FaLock, FaBolt, FaWallet, FaFileInvoiceDollar,
    FaSnowflake, FaExchangeAlt
} from 'react-icons/fa';
// Reutilizaremos los estilos del otro modal, ya que son muy similares
import './ModalRegistroPago.css';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { collection, query, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { normalizarCategoriasEgreso, calcularSaldosDeBolsas } from '../utils/bolsasFinancierasUtils';

const formatCurrency = (val) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val || 0);

const reorderOtrosLast = (list = []) => {
    const seen = new Set();
    const cleaned = [];
    let hasOtros = false;
    list.forEach((item) => {
        const norm = (item || '').toString().trim();
        if (!norm) return;
        if (norm.toLowerCase() === 'otros') {
            hasOtros = true;
            return;
        }
        const key = norm.toLowerCase();
        if (!seen.has(key)) {
            seen.add(key);
            cleaned.push(norm);
        }
    });
    if (hasOtros) cleaned.push('Otros');
    return cleaned;
};

const ModalRegistroEgreso = ({ onClose, onConfirm, nextFolio }) => {
    const [monto, setMonto] = useState('');
    const [fechaEgreso, setFechaEgreso] = useState(new Date().toISOString().split('T')[0]);
    const [categoria, setCategoria] = useState('Mantenimiento');
    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('');
    const [conceptoEspecifico, setConceptoEspecifico] = useState('');
    const [categoriasEstructuradas, setCategoriasEstructuradas] = useState([]);
    const [prioridad, setPrioridad] = useState('Media');
    const [descripcion, setDescripcion] = useState('');
    const [comprobante, setComprobante] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [proveedor, setProveedor] = useState('');
    const [proveedores, setProveedores] = useState([]);
    const [loadingProveedores, setLoadingProveedores] = useState(false);
    const [referenciaFactura, setReferenciaFactura] = useState('');
    const [referencia, setReferencia] = useState('');
    const [folio, setFolio] = useState('');
    const [metodoPago, setMetodoPago] = useState('Transferencia Bancaria');
    const [tipoGasto, setTipoGasto] = useState('Variable');
    const [categoriasEgreso, setCategoriasEgreso] = useState(reorderOtrosLast([
        'Mantenimiento', 'Servicios Públicos', 'Seguridad',
        'Administrativos', 'Limpieza', 'Jardineria', 'Seguros y Contingencias', 'Otros'
    ]));
    const [errorMessage, setErrorMessage] = useState('');
    const [showConfirmClose, setShowConfirmClose] = useState(false);
    const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
    const [proveedorMsg, setProveedorMsg] = useState('');
    const [showSugerencias, setShowSugerencias] = useState(false);
    const [ingresos, setIngresos] = useState([]);
    const [egresosList, setEgresosList] = useState([]);
    const [loadingBolsas, setLoadingBolsas] = useState(false);
    const { userData } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId;

    // Estados para Compensación desde Bolsas Congeladas
    const [activarCompensacion, setActivarCompensacion] = useState(false);
    const [bolsaDonanteId, setBolsaDonanteId] = useState('');
    const [montoCompensado, setMontoCompensado] = useState('');

    useEffect(() => {
        const fetchProveedores = async () => {
            if (!fraccionamientoId) return;
            try {
                setLoadingProveedores(true);
                const ref = collection(db, 'fraccionamientos', fraccionamientoId, 'proveedores');
                const q = query(ref, orderBy('nombreEmpresa'));
                const snap = await getDocs(q);
                const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setProveedores(list);
            } catch (err) {
                console.error('ModalRegistroEgreso: no se pudo cargar proveedores', err);
            } finally {
                setLoadingProveedores(false);
            }
        };
        const fetchConceptosEgreso = async () => {
            if (!fraccionamientoId) return;
            try {
                const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
                const snap = await getDoc(fraccRef);
                const cfg = snap.exists() ? snap.data().configuracionRecibos : null;
                const estructuradas = normalizarCategoriasEgreso(cfg?.categoriasEgreso, cfg?.conceptosEgresoList);
                setCategoriasEstructuradas(estructuradas);

                if (estructuradas.length > 0) {
                    const firstCat = estructuradas[0];
                    setCategoriaSeleccionada(firstCat.nombre);
                    setCategoria(firstCat.nombre);
                    const firstConc = firstCat.conceptos?.[0];
                    if (firstConc) {
                        const cNom = typeof firstConc === 'string' ? firstConc : firstConc.nombre;
                        const cTipo = (typeof firstConc === 'object' && firstConc.tipoGasto) ? firstConc.tipoGasto : 'Variable';
                        setConceptoEspecifico(cNom);
                        setTipoGasto(cTipo);
                    }
                } else {
                    const list = Array.isArray(cfg?.conceptosEgresoList)
                        ? cfg.conceptosEgresoList
                        : cfg?.conceptosEgreso && typeof cfg.conceptosEgreso === 'object'
                            ? Object.values(cfg.conceptosEgreso)
                            : null;
                    if (list && list.length) {
                        const reordered = reorderOtrosLast(list.filter(Boolean));
                        setCategoriasEgreso(reordered);
                        if (reordered[0]) setCategoria(reordered[0]);
                    }
                }
            } catch (err) {
                console.error('ModalRegistroEgreso: no se pudo cargar conceptos de egreso', err);
            }
        };
        const fetchFinanzas = async () => {
            if (!fraccionamientoId) return;
            try {
                setLoadingBolsas(true);
                const [snapIng, snapEg] = await Promise.all([
                    getDocs(collection(db, 'fraccionamientos', fraccionamientoId, 'ingresos')),
                    getDocs(collection(db, 'fraccionamientos', fraccionamientoId, 'egresos'))
                ]);
                setIngresos(snapIng.docs.map(d => ({ id: d.id, ...d.data() })));
                setEgresosList(snapEg.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (err) {
                console.warn('ModalRegistroEgreso: error cargando finanzas para bolsas', err);
            } finally {
                setLoadingBolsas(false);
            }
        };

        fetchProveedores();
        fetchConceptosEgreso();
        fetchFinanzas();
        if (typeof nextFolio === 'function') {
            const computed = nextFolio();
            setFolio(computed);
        }
    }, [fraccionamientoId]);

    const handleCategoriaChange = (nombreCat) => {
        setCategoriaSeleccionada(nombreCat);
        setCategoria(nombreCat);
        const catObj = categoriasEstructuradas.find(c => c.nombre === nombreCat);
        if (catObj && catObj.conceptos && catObj.conceptos.length > 0) {
            const primerConcepto = catObj.conceptos[0];
            const cNom = typeof primerConcepto === 'string' ? primerConcepto : primerConcepto.nombre;
            const cTipo = (typeof primerConcepto === 'object' && primerConcepto.tipoGasto) ? primerConcepto.tipoGasto : 'Variable';
            setConceptoEspecifico(cNom);
            setTipoGasto(cTipo);
        } else {
            setConceptoEspecifico('');
        }
    };

    const handleConceptoEspecificoChange = (cNom) => {
        setConceptoEspecifico(cNom);
        const catObj = categoriasEstructuradas.find(c => c.nombre === categoriaSeleccionada);
        if (catObj) {
            const concObj = (catObj.conceptos || []).find(c => (typeof c === 'string' ? c : c.nombre) === cNom);
            if (concObj && typeof concObj === 'object' && concObj.tipoGasto) {
                setTipoGasto(concObj.tipoGasto);
            } else {
                setTipoGasto('Variable');
            }
        }
    };

    // Mapeo en tiempo real de saldos disponibles por bolsa financiera
    const mapaSaldosBolsas = useMemo(() => {
        if (!categoriasEstructuradas.length) return {};
        return calcularSaldosDeBolsas(categoriasEstructuradas, ingresos, egresosList);
    }, [categoriasEstructuradas, ingresos, egresosList]);

    // Información e impacto en vivo de la bolsa seleccionada
    const infoBolsaSeleccionada = useMemo(() => {
        const catNombre = categoriaSeleccionada || categoria;
        if (!catNombre) return null;
        const info = mapaSaldosBolsas[catNombre];
        const catObj = categoriasEstructuradas.find(c => c.nombre === catNombre);
        const saldoActual = info?.saldoDisponible != null ? info.saldoDisponible : 0;
        const color = info?.color || catObj?.color || '#6366f1';
        const montoNum = parseFloat(monto || 0);
        const saldoDespues = Math.round((saldoActual - montoNum) * 100) / 100;
        const deficit = saldoDespues < 0 ? Math.abs(saldoDespues) : 0;
        const compVal = activarCompensacion ? Number(montoCompensado || 0) : 0;
        const saldoFinalCompensado = Math.round((saldoDespues + compVal) * 100) / 100;
        const esCompartida = Boolean(info?.esBolsaMadreCompartida);
        const vinculadas = info?.categoriasVinculadas || [];

        return {
            nombre: catNombre,
            color,
            saldoActual,
            saldoDespues,
            saldoFinalCompensado,
            deficit,
            montoEgreso: montoNum,
            esCompartida,
            vinculadas,
            totalCapacidad: info?.totalCapacidad || 0,
            totalEgresos: info?.totalEgresos || 0
        };
    }, [categoriaSeleccionada, categoria, mapaSaldosBolsas, categoriasEstructuradas, monto, activarCompensacion, montoCompensado]);

    // Lista de categorías que cuentan con fondos congelados disponibles para compensar
    const bolsasConSaldoCongelado = useMemo(() => {
        const catActual = categoriaSeleccionada || categoria;
        return categoriasEstructuradas
            .map(cat => {
                const info = mapaSaldosBolsas[cat.nombre];
                const saldoCongelado = info?.saldoCongelado != null ? info.saldoCongelado : Number(cat.congelacion?.saldoCongeladoAcumulado || 0);
                return {
                    id: cat.id,
                    nombre: cat.nombre,
                    color: cat.color || '#0284c7',
                    periodicidad: cat.congelacion?.periodicidad || 'mensual',
                    saldoCongelado
                };
            })
            .filter(b => b.saldoCongelado > 0 && b.nombre !== catActual);
    }, [categoriasEstructuradas, mapaSaldosBolsas, categoriaSeleccionada, categoria]);

    // Efecto para auto-seleccionar la primera bolsa donante y auto-llenar el faltante cuando se active la compensación
    useEffect(() => {
        if (!activarCompensacion) return;
        const deficit = infoBolsaSeleccionada?.deficit || 0;
        if (deficit <= 0) return;

        if (!bolsaDonanteId && bolsasConSaldoCongelado.length > 0) {
            const first = bolsasConSaldoCongelado[0];
            setBolsaDonanteId(first.id);
            setMontoCompensado(Math.min(deficit, first.saldoCongelado));
        } else if (bolsaDonanteId) {
            const donor = bolsasConSaldoCongelado.find(b => b.id === bolsaDonanteId);
            if (donor && (!montoCompensado || Number(montoCompensado) > donor.saldoCongelado)) {
                setMontoCompensado(Math.min(deficit, donor.saldoCongelado));
            }
        }
    }, [activarCompensacion, infoBolsaSeleccionada?.deficit, bolsaDonanteId, bolsasConSaldoCongelado, montoCompensado]);

    // Helper: Buscar proveedor por nombre, contacto, RFC, teléfono
    const buscarProveedor = (term) => {
        if (!term.trim()) return [];
        const lower = term.toLowerCase();
        return proveedores.filter((p) => {
            const nombre = (p.nombreEmpresa || '').toLowerCase();
            const contacto = (p.nombreContacto || '').toLowerCase();
            const rfc = (p.rfc || '').toLowerCase();
            const telefono = (p.telefono || '').toLowerCase();
            return nombre.includes(lower) || contacto.includes(lower) || rfc.includes(lower) || telefono.includes(lower);
        });
    };

    // Validar si el proveedor existe en la lista
    const matchedProveedores = useMemo(() => buscarProveedor(proveedor), [proveedor, proveedores]);

    useEffect(() => {
        if (!proveedor.trim()) {
            setProveedorSeleccionado(null);
            setProveedorMsg('');
            setShowSugerencias(false);
            return;
        }

        const exactMatch = proveedores.find(
            (p) => (p.nombreEmpresa || '').toLowerCase().trim() === proveedor.toLowerCase().trim()
        );

        if (exactMatch) {
            setProveedorSeleccionado(exactMatch);
            setProveedorMsg('✓ Proveedor encontrado en el sistema');
            setShowSugerencias(false);
        } else if (matchedProveedores.length > 0) {
            setProveedorSeleccionado(null);
            setProveedorMsg(`✓ ${matchedProveedores.length} coincidencia(s) encontrada(s)`);
            setShowSugerencias(true);
        } else {
            setProveedorSeleccionado(null);
            setProveedorMsg('⚠ Este proveedor no existe en el sistema. Puedes agregarlo para futuros egresos.');
            setShowSugerencias(false);
        }
    }, [proveedor, proveedores, matchedProveedores]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');

        // Validaciones detalladas
        if (!monto || monto === '' || monto <= 0) {
            setErrorMessage('⚠️ Monto del gasto: Campo obligatorio y debe ser mayor a 0.');
            return;
        }

        if (!fechaEgreso) {
            setErrorMessage('⚠️ Fecha del gasto: Campo obligatorio.');
            return;
        }

        if (!proveedor.trim()) {
            setErrorMessage('⚠️ Nombre del proveedor: Campo obligatorio.');
            return;
        }

        if (!referenciaFactura.trim()) {
            setErrorMessage('⚠️ Referencia/No. de factura: Campo obligatorio.');
            return;
        }

        // Descripción y Comprobante ahora son opcionales

        // Validar tipo de archivo (solo si hay comprobante)
        if (comprobante) {
            const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
            if (!validTypes.includes(comprobante.type)) {
                setErrorMessage('⚠️ Comprobante: Solo se aceptan archivos PDF o imágenes (JPG, PNG).');
                return;
            }

            // Validar tamaño del archivo (máximo 5MB)
            const maxSize = 5 * 1024 * 1024;
            if (comprobante.size > maxSize) {
                setErrorMessage('⚠️ Comprobante: El archivo no debe exceder 5MB.');
                return;
            }
        }

        if (activarCompensacion) {
            if (!bolsaDonanteId) {
                setErrorMessage('⚠️ Selecciona la bolsa donante de donde se extraerán los fondos congelados.');
                return;
            }
            const donorObj = bolsasConSaldoCongelado.find(b => b.id === bolsaDonanteId);
            const mComp = Number(montoCompensado);
            if (!montoCompensado || isNaN(mComp) || mComp <= 0) {
                setErrorMessage('⚠️ Ingresa un monto válido a extraer de la bolsa congelada.');
                return;
            }
            if (donorObj && mComp > donorObj.saldoCongelado) {
                setErrorMessage(`⚠️ El monto a compensar (${formatCurrency(mComp)}) supera los fondos congelados disponibles de "${donorObj.nombre}" (${formatCurrency(donorObj.saldoCongelado)}).`);
                return;
            }
        }

        // Si todas las validaciones pasan, continuamos con el envío
        setIsSubmitting(true);
        const catFinal = categoriaSeleccionada || categoria;
        const descFinal = descripcion.trim() || (conceptoEspecifico && conceptoEspecifico !== 'Otro' ? conceptoEspecifico : catFinal);
        const donorObj = activarCompensacion ? bolsasConSaldoCongelado.find(b => b.id === bolsaDonanteId) : null;
        await onConfirm({
            monto: Number(monto),
            fechaEgreso,
            categoria: catFinal,
            categoriaEgreso: catFinal,
            concepto: conceptoEspecifico || catFinal,
            descripcion: descFinal,
            comprobante,
            proveedor,
            referenciaFactura,
            referencia,
            folio,
            metodoPago,
            tipoGasto: tipoGasto || 'Variable',
            prioridad,
            compensacionBolsaCongelada: activarCompensacion && donorObj && Number(montoCompensado) > 0 ? {
                activa: true,
                bolsaDonanteId: donorObj.id,
                bolsaDonanteNombre: donorObj.nombre,
                montoCompensado: Number(montoCompensado),
                categoriaReceptora: catFinal
            } : null
        });
        setIsSubmitting(false);
        onClose();
    };

    const prioridades = ['Baja', 'Media', 'Alta', 'Urgente'];
    const metodosDePago = [
        'Transferencia Bancaria', 'Tarjeta de Crédito', 'Tarjeta de Débito',
        'PayPal', 'Mercado Pago', 'Efectivo', 'Pago Cajero', 'Otros'
    ];

    const hasCapturedData = () => {
        return (
            parseFloat(monto || 0) > 0 ||
            (proveedor && proveedor.trim()) ||
            (referenciaFactura && referenciaFactura.trim()) ||
            (descripcion && descripcion.trim()) ||
            comprobante ||
            (referencia && referencia.trim()) ||
            (folio && folio.trim()) ||
            (metodoPago && metodoPago !== 'Transferencia Bancaria') ||
            (categoria && categoria !== 'Mantenimiento') ||
            (prioridad && prioridad !== 'Media')
        );
    };

    const handleRequestClose = () => {
        if (hasCapturedData()) {
            setShowConfirmClose(true);
            return;
        }
        onClose();
    };

    const sanitizeNumberInput = (value, allowDecimal = true) => {
        const regex = allowDecimal ? /[^0-9.]/g : /[^0-9]/g;
        return (value || '').replace(regex, '');
    };

    const preventNonNumeric = (evt) => {
        const { data, inputType } = evt.nativeEvent;
        if (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward') return;
        if (!data) return;
        if (!/^[0-9.]$/.test(data)) {
            evt.preventDefault();
        }
    };

    const confirmCloseModal = () => {
        setShowConfirmClose(false);
        onClose();
    };

    const cancelCloseModal = () => {
        setShowConfirmClose(false);
    };

    return (
        <div
            className="pago-modal-wrapper"
            data-css-version="overlay-v3"
        >
            <div className="pago-modal-content">
                <button onClick={handleRequestClose} className="modal-close-btn"><FaTimes /></button>
                <div className="modal-header">
                    <h2>
                        <FaFileInvoiceDollar style={{ marginRight: '10px', verticalAlign: 'middle' }} />
                        Registrar Nuevo Egreso
                    </h2>
                    <div className="modal-header-meta">
                        {folio && (
                            <div className="modal-folio-info">
                                <span className="folio-label">Folio:</span>
                                <span className="folio-number">{folio}</span>
                            </div>
                        )}
                        {parseFloat(monto || 0) > 0 && (
                            <div className="modal-monto-preview">
                                <span className="monto-preview-label">Total Gasto:</span>
                                <span className="monto-preview-val">{formatCurrency(parseFloat(monto))}</span>
                            </div>
                        )}
                    </div>
                </div>

                {errorMessage && (
                    <div className="form-error-message">
                        <FaExclamationTriangle /> {errorMessage}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="form-layout">
                        <div className="form-column form-column--primary">
                            <div className="pagos-container">
                                <div className="pago-card">
                                    <div className="pago-header">
                                        <div>
                                            <p className="badge">Egreso</p>
                                            <h4>Detalle del gasto</h4>
                                        </div>
                                    </div>

                                    {/* Fila 1: Monto y Fecha */}
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label className="form-label-row">
                                                Monto del Gasto * <span className="help-icon" title="Monto total del gasto en MXN">?</span>
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                inputMode="decimal"
                                                pattern="[0-9]*[.,]?[0-9]*"
                                                value={monto}
                                                onChange={e => {
                                                    const clean = sanitizeNumberInput(e.target.value, true);
                                                    setMonto(clean === '' ? '' : parseFloat(clean));
                                                }}
                                                onBeforeInput={preventNonNumeric}
                                                placeholder="0.00"
                                                required
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label-row">
                                                Fecha del Gasto * <span className="help-icon" title="Fecha en que se realizó el gasto">?</span>
                                            </label>
                                            <input 
                                                type="date" 
                                                value={fechaEgreso} 
                                                onChange={e => setFechaEgreso(e.target.value)} 
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Fila 2: Categoría / Bolsa y Concepto Específico (con Chip Fijo/Variable) */}
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label className="form-label-row">
                                                Categoría / Bolsa * <span className="help-icon" title="Bolsa presupuestal que absorbe este gasto">?</span>
                                            </label>
                                            {categoriasEstructuradas.length > 0 ? (
                                                <select value={categoriaSeleccionada} onChange={e => handleCategoriaChange(e.target.value)}>
                                                    {categoriasEstructuradas.map(cat => (
                                                        <option key={cat.id || cat.nombre} value={cat.nombre}>
                                                            {cat.nombre}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <select value={categoria} onChange={e => setCategoria(e.target.value)}>
                                                    {categoriasEgreso.map(cat => (
                                                        <option key={cat} value={cat}>{cat}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label-row">
                                                <span>Concepto Específico *</span>
                                                <span className={`badge-tipo-gasto ${tipoGasto === 'Fijo' ? 'fijo' : 'variable'}`}>
                                                    {tipoGasto === 'Fijo' ? <><FaLock /> Fijo</> : <><FaBolt /> Variable</>}
                                                </span>
                                                <span className="help-icon" title="Concepto detallado. El tipo Fijo o Variable se asigna automáticamente.">?</span>
                                            </label>
                                            {categoriasEstructuradas.length > 0 ? (
                                                <select value={conceptoEspecifico} onChange={e => handleConceptoEspecificoChange(e.target.value)}>
                                                    {((categoriasEstructuradas.find(c => c.nombre === categoriaSeleccionada)?.conceptos) || []).map(c => {
                                                        const nom = typeof c === 'string' ? c : c.nombre;
                                                        const tip = (typeof c === 'object' && c.tipoGasto) ? ` (${c.tipoGasto})` : '';
                                                        return <option key={nom} value={nom}>{nom}{tip}</option>;
                                                    })}
                                                    <option value="Otro">Otro (especificar en descripción)</option>
                                                </select>
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={descripcion}
                                                    onChange={e => setDescripcion(e.target.value)}
                                                    placeholder="Detalle o concepto del gasto..."
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {/* Fila 3: Método de Pago y Prioridad (Sin el selector redundante de Tipo de Gasto) */}
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label className="form-label-row">
                                                Método de Pago <span className="help-icon" title="Forma de pago utilizada">?</span>
                                            </label>
                                            <select value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)}>
                                                {metodosDePago.map((m) => <option key={m} value={m}>{m}</option>)}
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label-row">
                                                Prioridad <span className="help-icon" title="Nivel de importancia o urgencia del gasto">?</span>
                                            </label>
                                            <select value={prioridad} onChange={e => setPrioridad(e.target.value)}>
                                                {prioridades.map(p => <option key={p} value={p}>{p}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Fila 4: Proveedor y No. Factura / Referencia */}
                                    <div className="form-grid">
                                        <div className="form-group" style={{ position: 'relative' }}>
                                            <label className="form-label-row">
                                                Proveedor * <span className="help-icon" title="Nombre, contacto, RFC o teléfono">?</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={proveedor}
                                                onChange={e => setProveedor(e.target.value)}
                                                onFocus={() => proveedor.trim() && matchedProveedores.length > 0 && setShowSugerencias(true)}
                                                placeholder={loadingProveedores ? 'Cargando proveedores...' : 'Empieza a escribir...'}
                                                autoComplete="off"
                                                required
                                            />
                                            {showSugerencias && matchedProveedores.length > 0 && (
                                                <div className="proveedor-sugerencias">
                                                    {matchedProveedores.slice(0, 5).map((p) => (
                                                        <div
                                                            key={p.id}
                                                            className="sugerencia-item"
                                                            onClick={() => {
                                                                setProveedor(p.nombreEmpresa);
                                                                setProveedorSeleccionado(p);
                                                                setShowSugerencias(false);
                                                            }}
                                                        >
                                                            <div className="sugerencia-nombre">{p.nombreEmpresa}</div>
                                                            {p.nombreContacto && <div className="sugerencia-detalle">Contacto: {p.nombreContacto}</div>}
                                                            {p.rfc && <div className="sugerencia-detalle">RFC: {p.rfc}</div>}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {proveedorMsg && (
                                                <small style={{
                                                    display: 'block',
                                                    marginTop: '6px',
                                                    color: proveedorSeleccionado ? '#16a34a' : matchedProveedores.length > 0 ? '#2563eb' : '#ea580c',
                                                    fontSize: '0.85em'
                                                }}>
                                                    {proveedorMsg}
                                                </small>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label-row">
                                                No. Factura/Referencia * <span className="help-icon" title="Número de factura o comprobante fiscal">?</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={referenciaFactura}
                                                onChange={e => setReferenciaFactura(e.target.value)}
                                                placeholder="Ej: FAC-1234 o FOL-882"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Fila 5: Referencia Interna y Botón Interactivo de Comprobante */}
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label className="form-label-row">
                                                Referencia Interna (opcional) <span className="help-icon" title="Folio o control contable interno">?</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={referencia}
                                                onChange={e => setReferencia(e.target.value)}
                                                placeholder="Ej: REF-INT-001"
                                            />
                                        </div>

                                        <div className="form-group form-group-file">
                                            <label className="form-label-row">
                                                Comprobante (opcional) <span className="help-icon" title="Factura o recibo en formato PDF o imagen">?</span>
                                            </label>
                                            <input
                                                type="file"
                                                id="comprobante-egreso-file"
                                                accept="image/*,.pdf"
                                                onChange={e => {
                                                    if (e.target.files && e.target.files[0]) {
                                                        setComprobante(e.target.files[0]);
                                                    }
                                                }}
                                                style={{ display: 'none' }}
                                            />
                                            <div className="custom-file-upload">
                                                <label htmlFor="comprobante-egreso-file" className="file-upload-button">
                                                    <FaUpload />
                                                    <span>{comprobante ? 'Cambiar archivo' : 'Adjuntar Comprobante'}</span>
                                                </label>
                                                {comprobante && (
                                                    <div className="file-name-badge">
                                                        <span className="file-icon-mini">
                                                            {comprobante.name?.toLowerCase().endsWith('.pdf') ? 'PDF' : 'IMG'}
                                                        </span>
                                                        <span className="file-name-text" title={comprobante.name}>
                                                            {comprobante.name}
                                                        </span>
                                                        <button 
                                                            type="button" 
                                                            className="btn-remove-file" 
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setComprobante(null);
                                                                const inputEl = document.getElementById('comprobante-egreso-file');
                                                                if (inputEl) inputEl.value = '';
                                                            }}
                                                            title="Quitar archivo"
                                                        >
                                                            <FaTimes />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <small className="cp-muted">Máximo 5MB • PDF, JPG o PNG</small>
                                        </div>
                                    </div>

                                    {/* Fila 6: Descripción amplia */}
                                    <div className="form-grid form-grid-notes">
                                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                            <label className="form-label-row">
                                                Descripción / Observaciones (opcional) <span className="help-icon" title="Detalle o justificación del gasto">?</span>
                                            </label>
                                            <textarea
                                                value={descripcion}
                                                onChange={e => setDescripcion(e.target.value)}
                                                rows="2"
                                                placeholder="Ej: Compra de insumos para pasillos o detalle del servicio..."
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Columna Derecha: Calculadora de Bolsa e Información */}
                        <aside className="form-column form-column--summary">
                            {/* Tarjeta Calculadora de Impacto en Bolsa */}
                            {infoBolsaSeleccionada && (
                                <div className="calculadora-bolsa-card">
                                    <div className="calc-bolsa-header" style={{ borderLeftColor: infoBolsaSeleccionada.color }}>
                                        <FaWallet style={{ color: infoBolsaSeleccionada.color, fontSize: '1.25rem' }} />
                                        <div>
                                            <span className="calc-bolsa-sub">Impacto en Bolsa Presupuestal</span>
                                            <h4>{infoBolsaSeleccionada.nombre}</h4>
                                        </div>
                                    </div>

                                    <div className="calc-bolsa-body">
                                        <div className="calc-row">
                                            <span className="calc-label">Saldo Disponible Actual:</span>
                                            <strong className={`calc-val ${infoBolsaSeleccionada.saldoActual > 0 ? 'positive' : 'negative'}`}>
                                                {loadingBolsas ? 'Calculando...' : formatCurrency(infoBolsaSeleccionada.saldoActual)}
                                            </strong>
                                        </div>
                                        <div className="calc-row">
                                            <span className="calc-label">Monto de este Egreso:</span>
                                            <strong className="calc-val egreso">
                                                - {formatCurrency(infoBolsaSeleccionada.montoEgreso)}
                                            </strong>
                                        </div>
                                        <div className="calc-divider" />
                                        <div className="calc-row destacada">
                                            <span className="calc-label">Saldo Estimado Después:</span>
                                            <strong className={`calc-val ${infoBolsaSeleccionada.saldoFinalCompensado >= 0 ? 'positive' : 'negative'}`}>
                                                {loadingBolsas ? 'Calculando...' : formatCurrency(infoBolsaSeleccionada.saldoFinalCompensado)}
                                            </strong>
                                        </div>

                                        {activarCompensacion && Number(montoCompensado) > 0 && (
                                            <div className="compensacion-activa-pill">
                                                <FaSnowflake style={{ marginRight: '5px' }} />
                                                <span>Compensado con fondos congelados: +{formatCurrency(Number(montoCompensado))}</span>
                                            </div>
                                        )}

                                        {!loadingBolsas && infoBolsaSeleccionada.saldoDespues < 0 && (
                                            <>
                                                <div className="alerta-deficit">
                                                    <FaExclamationTriangle />
                                                    <span>
                                                        Este egreso supera los fondos disponibles de la bolsa por <strong>{formatCurrency(infoBolsaSeleccionada.deficit)}</strong>.
                                                    </span>
                                                </div>

                                                {/* Panel Interactivo de Compensación desde Bolsa Congelada */}
                                                <div className="compensacion-congelada-panel">
                                                    <div className="compensacion-panel-header">
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                                            <FaSnowflake style={{ color: '#0284c7' }} />
                                                            <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#0369a1' }}>
                                                                Compensar con Bolsa Congelada
                                                            </span>
                                                        </div>
                                                        {bolsasConSaldoCongelado.length > 0 && (
                                                            <label className="bolsa-switch" title="Activar compensación desde bolsa congelada">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={activarCompensacion}
                                                                    onChange={(e) => setActivarCompensacion(e.target.checked)}
                                                                />
                                                                <span className="bolsa-slider" />
                                                            </label>
                                                        )}
                                                    </div>

                                                    {bolsasConSaldoCongelado.length === 0 ? (
                                                        <p style={{ margin: '8px 0 0', fontSize: '0.78rem', color: '#64748b' }}>
                                                            No hay otras bolsas con fondos congelados de reserva disponibles en este momento.
                                                        </p>
                                                    ) : activarCompensacion ? (
                                                        <div className="compensacion-panel-body">
                                                            <div className="form-group" style={{ marginBottom: '8px' }}>
                                                                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                                                                    Bolsa Donante (Fondos Congelados):
                                                                </label>
                                                                <select
                                                                    value={bolsaDonanteId}
                                                                    onChange={(e) => setBolsaDonanteId(e.target.value)}
                                                                    style={{ width: '100%', padding: '7px 10px', fontSize: '0.82rem', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                                                >
                                                                    {bolsasConSaldoCongelado.map(b => (
                                                                        <option key={b.id} value={b.id}>
                                                                            {b.nombre} ({formatCurrency(b.saldoCongelado)} disp. · {b.periodicidad})
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>

                                                            <div className="form-group" style={{ marginBottom: '8px' }}>
                                                                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                                                                    Monto a extraer de la bolsa congelada ($ MXN):
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    min="0.01"
                                                                    max={bolsasConSaldoCongelado.find(b => b.id === bolsaDonanteId)?.saldoCongelado || 0}
                                                                    value={montoCompensado}
                                                                    onChange={(e) => setMontoCompensado(e.target.value)}
                                                                    style={{ width: '100%', padding: '7px 10px', fontSize: '0.82rem', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                                                    placeholder="0.00"
                                                                />
                                                                <small style={{ fontSize: '0.72rem', color: '#0284c7', marginTop: '3px', display: 'block' }}>
                                                                    Faltante a cubrir: {formatCurrency(infoBolsaSeleccionada.deficit)}
                                                                </small>
                                                            </div>

                                                            {bolsaDonanteId && (
                                                                <div className="compensacion-preview-resumen">
                                                                    <span style={{ fontSize: '0.75rem', color: '#475569' }}>Remanente congelado en donante:</span>
                                                                    <strong style={{ fontSize: '0.82rem', color: '#0369a1' }}>
                                                                        {formatCurrency(
                                                                            Math.max(
                                                                                0,
                                                                                (bolsasConSaldoCongelado.find(b => b.id === bolsaDonanteId)?.saldoCongelado || 0) - Number(montoCompensado || 0)
                                                                            )
                                                                        )}
                                                                    </strong>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <p style={{ margin: '8px 0 0', fontSize: '0.78rem', color: '#0369a1' }}>
                                                            Puedes activar el switch para extraer el faltante ({formatCurrency(infoBolsaSeleccionada.deficit)}) de una bolsa con fondos congelados disponibles.
                                                        </p>
                                                    )}
                                                </div>
                                            </>
                                        )}

                                        {infoBolsaSeleccionada.esCompartida && infoBolsaSeleccionada.vinculadas.length > 0 && (
                                            <div className="nota-fondo-comun">
                                                <FaInfoCircle />
                                                <span>
                                                    Fondo común compartido con: <strong>{infoBolsaSeleccionada.vinculadas.join(', ')}</strong>
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {proveedorSeleccionado && (
                                <div className="proveedor-resumen-card">
                                    <p className="proveedor-titulo">📋 Datos del Proveedor</p>
                                    <div className="proveedor-dato">
                                        <span className="etiqueta">Nombre:</span>
                                        <span className="valor">{proveedorSeleccionado.nombreEmpresa || 'N/A'}</span>
                                    </div>
                                    {proveedorSeleccionado.nombreContacto && (
                                        <div className="proveedor-dato">
                                            <span className="etiqueta">Contacto:</span>
                                            <span className="valor">{proveedorSeleccionado.nombreContacto}</span>
                                        </div>
                                    )}
                                    {proveedorSeleccionado.telefono && (
                                        <div className="proveedor-dato">
                                            <span className="etiqueta">Teléfono:</span>
                                            <span className="valor">{proveedorSeleccionado.telefono}</span>
                                        </div>
                                    )}
                                    {proveedorSeleccionado.rfc && (
                                        <div className="proveedor-dato">
                                            <span className="etiqueta">RFC:</span>
                                            <span className="valor">{proveedorSeleccionado.rfc}</span>
                                        </div>
                                    )}
                                    {proveedorSeleccionado.usoCFID && (
                                        <div className="proveedor-dato">
                                            <span className="etiqueta">Uso CFID:</span>
                                            <span className="valor">{proveedorSeleccionado.usoCFID}</span>
                                        </div>
                                    )}
                                    {proveedorSeleccionado.regimenFiscal && (
                                        <div className="proveedor-dato">
                                            <span className="etiqueta">Régimen:</span>
                                            <span className="valor">{proveedorSeleccionado.regimenFiscal}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="tips-card">
                                <p className="tips-title">Ayuda rápida</p>
                                <ul>
                                    <li>Campos con asterisco (*) son obligatorios.</li>
                                    <li>El tipo de gasto (Fijo o Variable) se determina automáticamente.</li>
                                    <li>El comprobante admite formatos PDF, JPG y PNG de hasta 5MB.</li>
                                    <li>La calculadora lateral muestra cómo se amortizará el fondo de la bolsa.</li>
                                </ul>
                            </div>
                        </aside>
                    </div>

                    <div className="modal-actions">
                        <button type="button" onClick={handleRequestClose} className="btn-cancel" disabled={isSubmitting}>Cancelar</button>
                        <button type="submit" className="btn-confirm" disabled={isSubmitting} style={{ backgroundColor: '#d9534f' }}>
                            {isSubmitting ? 'Registrando...' : 'Registrar Gasto'}
                        </button>
                    </div>
                </form>
            </div>

            {showConfirmClose && (
                <div className="confirm-leave-backdrop">
                    <div className="confirm-leave-modal">
                        <h4>¿Cerrar el formulario?</h4>
                        <p>Los datos capturados se perderán y tendrás que volver a empezar.</p>
                        <div className="confirm-leave-actions">
                            <button type="button" className="btn-secondary" onClick={cancelCloseModal}>Seguir editando</button>
                            <button type="button" className="btn-danger" onClick={confirmCloseModal}>Salir y descartar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModalRegistroEgreso;